import { Helmet } from "react-helmet";
// src/SubmitReviewPage.tsx

import React, { useState } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Rating,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import AddressAutocomplete from "../components/AddressAutocomplete";
import { postReview } from "../helpers/api";

interface FormData {
  address: string;
  fromDate: string; // YYYY-MM-DD
  toDate: string; // YYYY-MM-DD
  managementRating: number;
  areaRating: number;
  safetyRating: number;
  maintenanceRating: number;
  amenitiesRating: number;
  reviewText: string;
  lat: number;
  lng: number;
}

const SubmitReviewPage: React.FC = () => {
  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: {
      address: "",
      fromDate: "",
      toDate: "",
      managementRating: 0,
      areaRating: 0,
      safetyRating: 0,
      maintenanceRating: 0,
      amenitiesRating: 0,
      reviewText: "",
      lat: 0,
      lng: 0,
    },
  });

  const [loading, setLoading] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const fromDate = watch("fromDate");

  const onSubmit = async (data: FormData) => {
    setLoading(true);
    setErrorMsg(null);
    try {
      await postReview({
        address: data.address,
        fromDate: data.fromDate,
        toDate: data.toDate,
        managementRating: data.managementRating,
        areaRating: data.areaRating,
        safetyRating: data.safetyRating,
        maintenanceRating: data.maintenanceRating,
        amenitiesRating: data.amenitiesRating,
        reviewText: data.reviewText,
        latitude: data.lat,
        longitude: data.lng,
      });
      setSuccessOpen(true);
      // reset form fields
      setValue("address", "");
      setValue("fromDate", "");
      setValue("toDate", "");
      setValue("managementRating", 0);
      setValue("areaRating", 0);
      setValue("safetyRating", 0);
      setValue("maintenanceRating", 0);
      setValue("amenitiesRating", 0);
      setValue("reviewText", "");
      setValue("lat", 0);
      setValue("lng", 0);
    } catch (err: any) {
      setErrorMsg(err.message || "Failed to submit review.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Submit a Review | Rate My Cribs</title>
        <meta
          name="description"
          content="Share your experience with an apartment or rental property. Submit a review to help other renters make informed decisions and find the best places to live."
        />
        <meta
          name="keywords"
          content="submit review, apartment review, rental property, tenant feedback, rate my cribs, landlord rating, honest review"
        />
        <link rel="canonical" href="https://ratemycribs.com/submit" />
        <meta property="og:title" content="Submit a Review | Rate My Cribs" />
        <meta
          property="og:description"
          content="Share your experience with an apartment or rental property. Submit a review to help other renters make informed decisions and find the best places to live."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://ratemycribs.com/submit" />
        <meta
          property="og:image"
          content="https://ratemycribs.com/og-image.png"
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Submit a Review | Rate My Cribs" />
        <meta
          name="twitter:description"
          content="Share your experience with an apartment or rental property. Submit a review to help other renters make informed decisions and find the best places to live."
        />
        <meta
          name="twitter:image"
          content="https://ratemycribs.com/og-image.png"
        />
      </Helmet>
      <Box
        component="main"
        id="main-content"
        tabIndex={-1}
        sx={{
          flex: 1,
          width: "100%",
          minHeight: 0,
          py: 6,
          m: 2,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 5 },
            maxWidth: 560,
            mx: "auto",
            borderRadius: 4,
            border: "1px solid",
            borderColor: "divider",
            boxShadow: (t) =>
              `0 24px 64px ${t.palette.mode === "light" ? "rgba(15,23,42,0.08)" : "rgba(0,0,0,0.35)"}`,
            animation: "slideUp 0.55s ease-out both",
            "@media (prefers-reduced-motion: reduce)": {
              animation: "none",
            },
            "@media (prefers-reduced-motion: no-preference)": {
              transition: "box-shadow 0.35s ease, transform 0.35s ease",
              "&:hover": {
                boxShadow: (t) =>
                  `0 28px 72px ${t.palette.mode === "light" ? "rgba(15,23,42,0.1)" : "rgba(0,0,0,0.4)"}`,
                transform: "translateY(-2px)",
              },
            },
          }}
        >
          <Typography
            variant="h4"
            fontWeight={800}
            color="primary"
            align="center"
            gutterBottom
          >
            Submit Property Review
          </Typography>
          <Typography
            variant="body1"
            align="center"
            color="text.secondary"
            sx={{ mb: 2 }}
          >
            Enter the address of the property you want to review, fill out the
            form below, and click <b>Submit Review</b> at the bottom.
          </Typography>
          <Typography
            style={{
              maxWidth: "max-content",
              marginLeft: "auto",
              marginRight: "auto",
            }}
          >
            Your feedback helps future renters!
          </Typography>
          <form onSubmit={handleSubmit(onSubmit)}>
            {/* Address */}
            <Controller
              name="address"
              control={control}
              rules={{ required: "Address is required" }}
              render={({ field }) => (
                <>
                  <AddressAutocomplete
                    value={field.value}
                    onChange={field.onChange}
                    onSelect={(addr, lat, lng) => {
                      field.onChange(addr);
                      setValue("lat", lat);
                      setValue("lng", lng);
                    }}
                    placeholder="Enter Address"
                    error={!!errors.address}
                  />
                  {errors.address && (
                    <Typography color="error" variant="caption">
                      {errors.address.message}
                    </Typography>
                  )}
                </>
              )}
            />

            {/* From Date */}
            <Controller
              name="fromDate"
              control={control}
              rules={{ required: "From date is required" }}
              render={({ field }) => (
                <TextField
                  {...field}
                  type="date"
                  label="From Date"
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  margin="normal"
                  error={!!errors.fromDate}
                  helperText={errors.fromDate?.message}
                />
              )}
            />

            {/* To Date (min 1 month after From) */}
            <Controller
              name="toDate"
              control={control}
              rules={{
                required: "To date is required",
                validate: (value) => {
                  if (!fromDate) return "Select From date first";
                  const from = new Date(fromDate);
                  const to = new Date(value);
                  if (to <= from) return "To date must be after From date";
                  const diffDays =
                    (to.getTime() - from.getTime()) / (1000 * 60 * 60 * 24);
                  if (diffDays < 30)
                    return "You must have lived at least one month";
                  return true;
                },
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  type="date"
                  label="To Date"
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  margin="normal"
                  error={!!errors.toDate}
                  helperText={errors.toDate?.message}
                />
              )}
            />

            {/* Star Ratings */}
            <Box
              mt={2}
              display="grid"
              gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }}
              gap={2}
            >
              {[
                { name: "managementRating", label: "Management" },
                { name: "areaRating", label: "Area" },
                { name: "safetyRating", label: "Safety" },
                { name: "maintenanceRating", label: "Maintenance" },
                { name: "amenitiesRating", label: "Amenities" },
              ].map(({ name, label }) => (
                <Controller
                  key={name}
                  name={name as any}
                  control={control}
                  rules={{
                    validate: (v) => v > 0 || `${label} rating is required`,
                  }}
                  render={({ field }) => (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <Typography
                        id={`rating-label-${name}`}
                        fontWeight={600}
                        color="primary"
                        component="p"
                        sx={{ mb: 0.5 }}
                      >
                        {label}
                      </Typography>
                      <Rating
                        {...field}
                        value={field.value}
                        onChange={(_, val) => field.onChange(val ?? 0)}
                        getLabelText={(v) =>
                          `${label}: ${v} star${v === 1 ? "" : "s"} of 5`
                        }
                        aria-labelledby={`rating-label-${name}`}
                      />
                      {errors[name as keyof FormData] && (
                        <Typography color="error" variant="caption">
                          {(errors[name as keyof FormData]
                            ?.message as string) ||
                            `${label} rating is required`}
                        </Typography>
                      )}
                    </Box>
                  )}
                />
              ))}
            </Box>

            {/* Review Text (required) */}
            <Controller
              name="reviewText"
              control={control}
              rules={{ required: "Review is required" }}
              render={({ field }) => (
                <>
                  <TextField
                    {...field}
                    label="Review"
                    multiline
                    rows={4}
                    fullWidth
                    margin="normal"
                    error={!!errors.reviewText}
                  />
                  {errors.reviewText && (
                    <Typography color="error" variant="caption">
                      {errors.reviewText.message}
                    </Typography>
                  )}
                </>
              )}
            />

            {/* Submit Button */}
            <Box mt={3} textAlign="center">
              <Button
                type="submit"
                variant="contained"
                color="primary"
                size="large"
                sx={{
                  borderRadius: 2,
                  px: 5,
                  fontWeight: 700,
                  fontSize: 18,
                  boxShadow: 2,
                }}
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} /> : "Submit Review"}
              </Button>
            </Box>
          </form>
        </Paper>

        {/* Success Snackbar */}
        <Snackbar
          open={successOpen}
          autoHideDuration={3000}
          onClose={() => setSuccessOpen(false)}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert severity="success">Review submitted!</Alert>
        </Snackbar>

        {/* Error Snackbar */}
        <Snackbar
          open={!!errorMsg}
          autoHideDuration={6000}
          onClose={() => setErrorMsg(null)}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert severity="error">{errorMsg}</Alert>
        </Snackbar>
      </Box>
    </>
  );
};

export default SubmitReviewPage;
